export const cityPages = {
  brisbane: {
    slug: 'brisbane',
    name: 'Brisbane',
    title: 'Is It a Day For It in Brisbane?',
    description:
      'Check whether Brisbane is serving park weather or pub weather today, plus what conditions usually tip the call either way.',
    intro:
      'Brisbane has a high ceiling for a great park day, but it can turn quickly once humidity climbs or an afternoon storm rolls over the river.',
    parkWeather:
      'A Brisbane park day usually means warm but not brutal heat, manageable humidity, and enough breeze to keep the afternoon comfortable.',
    pubWeather:
      'Once the air feels sticky, the sun gets punishing, or the storm cells start building, the answer changes fast. That is when a shaded pub becomes the smarter move.',
    localAngle:
      'Riverfront lawns, botanic-garden-style spaces, and late-afternoon hangs work especially well here because Brisbane rewards a good weather window more than an all-day plan.',
    watchFor: [
      'afternoon storm buildup after a clear morning',
      'humidity that makes a picnic feel heavier than the temperature suggests',
      'hot still air with no breeze',
    ],
    worksBestFor: [
      'group chats trying to lock a last-minute plan',
      'river walks, park hangs, and outdoor drinks before sunset',
      'deciding whether to commit to the outdoors or go straight indoors',
    ],
  },
  melbourne: {
    slug: 'melbourne',
    name: 'Melbourne',
    title: 'Is It a Day For It in Melbourne?',
    description:
      'Check whether Melbourne is a day for the park or a day for the pub, with local context around wind, temperature swings, and weather mood shifts.',
    intro:
      'Melbourne can hand you sun, wind, cloud, and a change of plan in the same afternoon, so the call matters more here than in most cities.',
    parkWeather:
      'A Melbourne park day usually needs a comfortable temperature band, reasonable sun, and low enough wind that you can actually sit outside without fighting for control of the napkins.',
    pubWeather:
      'If the southerly arrives, the cloud drops the temperature, or the drizzle starts doing that annoying half-rain thing, pub weather takes over immediately.',
    localAngle:
      'This city rewards flexibility. The best version of the product here is not perfect weather prediction, it is helping the group chat commit before everyone defaults to “whatever”.',
    watchFor: [
      'cold wind even when the sun is out',
      'fast temperature drops late in the day',
      'drizzle that makes an outdoor plan technically possible but clearly worse',
    ],
    worksBestFor: [
      'park hangs when the city gives you a clean weather window',
      'beer-garden decisions where wind matters as much as temperature',
      'friends who need a tie-breaker more than a forecast lecture',
    ],
  },
  sydney: {
    slug: 'sydney',
    name: 'Sydney',
    title: 'Is It a Day For It in Sydney?',
    description:
      'Check whether Sydney is serving ideal park weather or classic pub weather today, with local context around sun, storms, and harbour-side plans.',
    intro:
      'Sydney gets plenty of strong outdoor days, but once glare, heat, or rain moves in, the line between beach-adjacent optimism and pub realism gets pretty thin.',
    parkWeather:
      'A Sydney park day usually means bright weather, enough breeze to keep things pleasant, and conditions that still feel good after the first half hour outdoors.',
    pubWeather:
      'If the heat is sharp, the rain is moving through, or the wind turns an outdoor plan into work, a pub with indoor cover wins the argument quickly.',
    localAngle:
      'The best Sydney use case is deciding whether to commit to a harbourside or park-based catch-up, or cut straight to a venue before transport and indecision eat the day.',
    watchFor: [
      'harsh midday sun that makes “nice weather” less usable than it sounds',
      'showers that interrupt otherwise good conditions',
      'gusts that ruin exposed outdoor setups',
    ],
    worksBestFor: [
      'harbour walks, picnic spots, and outdoor meetups',
      'beer-garden fallback plans that still feel social',
      'tourists and locals trying to make a fast call before heading out',
    ],
  },
  perth: {
    slug: 'perth',
    name: 'Perth',
    title: 'Is It a Day For It in Perth?',
    description:
      'Check whether Perth is looking like proper park weather or whether the pub is the safer call, with local context around heat, wind, and late light.',
    intro:
      'Perth delivers huge outdoor upside, but dry heat and exposed afternoon conditions can turn a promising plan into a short stay fast.',
    parkWeather:
      'A Perth park day usually means warmth, low rain risk, and enough breeze to stop the afternoon from feeling like a commitment.',
    pubWeather:
      'Once the heat is sharp or the exposed open-air spots start feeling punishing, the better move is to go somewhere with shade, cold taps, and less sun on your face.',
    localAngle:
      'Perth really rewards late-afternoon timing. The difference between a great park session and a bad one is often just whether you waited for the harsher part of the day to pass.',
    watchFor: [
      'dry heat that feels heavier than the raw number suggests',
      'exposed parks with little natural shade',
      'afternoon conditions that improve sharply toward sunset',
    ],
    worksBestFor: [
      'post-work outdoor hangs',
      'sunset beers with enough breeze to stay comfortable',
      'making the call before you commit to a long drive',
    ],
  },
  adelaide: {
    slug: 'adelaide',
    name: 'Adelaide',
    title: 'Is It a Day For It in Adelaide?',
    description:
      'Check whether Adelaide is better suited to park beers or pub beers today, with local context around heat, dry conditions, and easy last-minute plans.',
    intro:
      'Adelaide often looks calm on paper, but the difference between pleasant and too exposed can still be the whole decision.',
    parkWeather:
      'A good Adelaide park day usually means dry air, manageable temperature, and a calm enough setup that you can sit outside without chasing shade every ten minutes.',
    pubWeather:
      'When the heat pushes too high or the comfort just is not there, the pub wins because the whole point was to relax, not endure it.',
    localAngle:
      'This city suits low-friction plans, so a fast verdict matters. If conditions are right, a park is easy. If not, a venue is usually the better use of the day.',
    watchFor: [
      'dry heat that creeps up on you',
      'strong sun with nowhere to hide',
      'late-day temperature staying higher than expected',
    ],
    worksBestFor: [
      'casual park hangs near the city',
      'easy last-minute drinks plans',
      'avoiding the false optimism of “it looks fine enough”',
    ],
  },
  'gold-coast': {
    slug: 'gold-coast',
    name: 'Gold Coast',
    title: 'Is It a Day For It on the Gold Coast?',
    description:
      'Check whether the Gold Coast is giving you park weather or pub weather today, with local context around humidity, storms, and outdoor plans.',
    intro:
      'The Gold Coast can look like an automatic outdoor day, but humidity and storm risk can make the actual experience much less clean than the forecast headline suggests.',
    parkWeather:
      'A Gold Coast park day usually means bright weather, enough air movement, and no sense that the sky is quietly setting up an afternoon interruption.',
    pubWeather:
      'Once it gets sticky, stormy, or too exposed, the move changes quickly. You want to be somewhere social, but not at the mercy of the weather.',
    localAngle:
      'Because so many plans here are already outdoor-leaning, the value is in knowing when to stop pretending the weather will hold and pivot early.',
    watchFor: [
      'afternoon storms on otherwise attractive days',
      'humidity that drains the fun out of staying outside',
      'tourist-heavy spots that feel worse when the weather is only half-good',
    ],
    worksBestFor: [
      'weekend group plans',
      'beach-adjacent park sessions',
      'calling the switch to a venue before the weather does it for you',
    ],
  },
  canberra: {
    slug: 'canberra',
    name: 'Canberra',
    title: 'Is It a Day For It in Canberra?',
    description:
      'Check whether Canberra is a genuine park day or whether the pub makes more sense, with local context around cooler air, wind, and seasonality.',
    intro:
      'Canberra rewards the right day more than most places, but it also punishes optimistic outdoor plans when the air is crisp and the wind shows up.',
    parkWeather:
      'A Canberra park day usually needs real sun, comfortable feels-like temperature, and enough calm to make sitting outside feel easy rather than brave.',
    pubWeather:
      'If it is cold in the shade, windy across open space, or the warmth is only theoretical, that is pub weather even if the forecast sounds respectable.',
    localAngle:
      'This is a city where “technically sunny” often is not enough. The product works best when it filters out that false positive and gives you a cleaner call.',
    watchFor: [
      'cold shade despite blue sky',
      'wind cutting through otherwise decent conditions',
      'big difference between midday and late afternoon comfort',
    ],
    worksBestFor: [
      'lunch breaks and after-work outdoor calls',
      'spring and autumn plans that depend on comfort more than forecast optimism',
      'choosing certainty over debating the weather in circles',
    ],
  },
  hobart: {
    slug: 'hobart',
    name: 'Hobart',
    title: 'Is It a Day For It in Hobart?',
    description:
      'Check whether Hobart feels like a park day or a pub day today, with local context around wind, cool air, and clear-but-deceptive conditions.',
    intro:
      'Hobart can look beautiful and still not be particularly pleasant for sitting outside with a drink, which makes the verdict more useful than the forecast alone.',
    parkWeather:
      'A Hobart park day usually needs clear comfort, not just clear skies. Sun helps, but shelter and wind matter just as much.',
    pubWeather:
      'If the breeze sharpens the air or the comfort falls away after a few minutes outside, the pub is the more honest answer.',
    localAngle:
      'This is one of the best cities for a “looks fine, feels bad” correction. That is exactly where the product earns its keep.',
    watchFor: [
      'cool wind turning a nice-looking day into a short stay',
      'fast temperature drop later in the day',
      'conditions that are scenic but not actually comfortable',
    ],
    worksBestFor: [
      'making a cleaner call on borderline days',
      'tourists deciding whether to stay outdoors or head in',
      'friends trying to avoid overcommitting to the weather',
    ],
  },
  newcastle: {
    slug: 'newcastle',
    name: 'Newcastle',
    title: 'Is It a Day For It in Newcastle?',
    description:
      'Check whether Newcastle is a proper day for park beers or whether the pub is the better move, with local context around coast wind and changing conditions.',
    intro:
      'Newcastle can offer great outdoor energy, but coastal wind and changing weather can turn an easy plan into a patchy one.',
    parkWeather:
      'A Newcastle park day usually means decent warmth, low rain risk, and wind that is noticeable but not annoying.',
    pubWeather:
      'If the coast is too gusty or the sky is unstable enough to keep you second-guessing the plan, pub weather has probably won.',
    localAngle:
      'It is one of those cities where the social plan often wants to stay outdoors, so the decision matters most on the fringe days rather than the obvious ones.',
    watchFor: [
      'coastal gusts that make exposed spots feel worse',
      'showers interrupting otherwise workable weather',
      'conditions that sound fine until you get to the foreshore',
    ],
    worksBestFor: [
      'coastal catch-ups',
      'group plans that could go either way',
      'making an earlier call instead of reacting late',
    ],
  },
};

export const featuredCities = [
  {
    slug: 'brisbane',
    teaser: 'Heat, humidity, storms, and the race to beat the late-afternoon mood swing.',
  },
  {
    slug: 'melbourne',
    teaser: 'Wind, sudden change, and the fastest park-to-pub reversals in the country.',
  },
  {
    slug: 'sydney',
    teaser: 'Harbour optimism, big sun, and the point where outdoors stops being comfortable.',
  },
  {
    slug: 'perth',
    teaser: 'Dry heat, long light, and the difference between warm and too exposed.',
  },
  {
    slug: 'adelaide',
    teaser: 'Low-friction plans, dry air, and whether the comfort is actually there.',
  },
  {
    slug: 'gold-coast',
    teaser: 'Humidity, storm risk, and when outdoor optimism needs a backup.',
  },
];
